'use client'

import { useState, useEffect, useMemo } from 'react'
import { useCurrentWorkspace } from '@/hooks/useCurrentWorkspace'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { createClient } from '@/utils/supabase/client'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tables, Enums } from '@/utils/schema'
import { CreditCardIcon } from '@hugeicons-pro/core-solid-rounded'
import { Alert01Icon, InformationCircleIcon, Loading03Icon } from '@hugeicons-pro/core-stroke-rounded'
import { AvatarUploadTrigger } from '@/components/AvatarUploadTrigger'
import ColorThief from 'colorthief'
import { HugeiconsIcon } from '@hugeicons/react'


interface WorkspaceSettingsPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAvatarUpdated?: () => void
  defaultOpenSection?: string
}

export function WorkspaceSettingsPanel({ open, onOpenChange, onAvatarUpdated, defaultOpenSection = "general" }: WorkspaceSettingsPanelProps) {
  const { workspace: currentOrg } = useCurrentWorkspace()
  const { reloadWorkspaces } = useWorkspace()
  
  // General settings state
  const [displayName, setDisplayName] = useState('')
  const [avatarPath, setAvatarPath] = useState<string | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  
  // Avatar cropping state
  const [croppedFile, setCroppedFile] = useState<File | null>(null)
  const [avatarCleared, setAvatarCleared] = useState(false)

  // Team settings state
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [teamInvitations, setTeamInvitations] = useState<any[]>([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Enums<'workspace_role'>>('member')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [teamLoading, setTeamLoading] = useState(false)
  const [callerRole, setCallerRole] = useState<Enums<'workspace_role'> | null>(null)
  const [ownerCount, setOwnerCount] = useState(0)
  const [pendingMemberAction, setPendingMemberAction] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; name: string; self: boolean } | null>(null)
  const [confirmCancelInvite, setConfirmCancelInvite] = useState<{ id: string; email: string } | null>(null)

  // Billing settings state
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null)
  const [billingLoading, setBillingLoading] = useState(false)


  // Accordion state
  const [activeSection, setActiveSection] = useState(defaultOpenSection)

  // Update active section when defaultOpenSection prop changes
  useEffect(() => {
    setActiveSection(defaultOpenSection)
  }, [defaultOpenSection])

  // Helper function to get user avatar URL
  const getUserAvatarUrl = (avatarPath: string | null) => {
    if (!avatarPath) return undefined
    const supabase = createClient()
    return supabase.storage.from('avatars').getPublicUrl(avatarPath).data.publicUrl
  }

  // Initialize data when workspace changes (but don't fetch team data until panel is opened)
  useEffect(() => {
    if (currentOrg) {
      // General settings
      setDisplayName(currentOrg.display_name || '')
      setAvatarPath(currentOrg.avatar_path)
      if (currentOrg.avatar_path) {
        // Use the same URL pattern as the sidebar
        const avatarUrl = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/workspace-avatars/${currentOrg.avatar_path}`
        setAvatarUrl(avatarUrl)
      } else {
        setAvatarUrl(null)
      }

      // Reset avatar state when workspace changes
      setCroppedFile(null)
      setAvatarCleared(false)

      // Billing settings
      setStripeCustomerId(currentOrg.stripe_customer_id)

      // Only fetch team data if panel is open
      if (open) {
        fetchTeam()
      }
    }
  }, [currentOrg, open])

  // Fetch team data when panel is opened
  useEffect(() => {
    if (open && currentOrg) {
      fetchTeam()
    }
  }, [open, currentOrg])

  // Fetch team members and invitations
  const fetchTeam = async () => {
    if (!currentOrg) return
    setTeamLoading(true)
    try {
      const res = await fetch(`/api/workspaces/team?workspaceId=${currentOrg.id}`)
      if (res.ok) {
        const data = await res.json()
        setTeamMembers(data.members || [])
        setTeamInvitations(data.invitations || [])
        setCallerRole(data.caller?.role ?? null)
        setOwnerCount(data.owner_count ?? 0)
      } else {
        // Don't silently render "No members yet" when the request failed.
        // Surface to the user so we never hide a real backend regression.
        let message = `Failed to load team (HTTP ${res.status})`
        try {
          const body = await res.json()
          if (body?.error) message = body.error
        } catch {}
        toast.error(message)
      }
    } catch (err) {
      console.error('Failed to load team:', err)
      toast.error('Failed to load team')
    }
    setTeamLoading(false)
  }

  // Avatar cropping handlers
  const handleCropComplete = (file: File) => {
    setCroppedFile(file);
    setAvatarCleared(false); // Reset cleared flag when new avatar is selected
  };

  // General settings handlers
  const handleSave = async () => {
    if (!currentOrg) return
    setLoading(true)
    
    try {
      const supabase = createClient()
      
      // Handle avatar deletion if cleared
      if (avatarCleared && avatarPath) {
        // Use API route to delete avatar
        const formData = new FormData()
        formData.append('action', 'delete')

        const deleteResponse = await fetch(`/api/workspaces/${currentOrg.id}/avatar`, {
          method: 'POST',
          body: formData
        })

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json()
          throw new Error(errorData.error || 'Failed to delete avatar')
        }

        // Update workspace display name if it has changed
        if (displayName !== currentOrg.display_name) {
          const { error: updateError } = await supabase
            .from('workspaces')
            .update({ display_name: displayName })
            .eq('id', currentOrg.id)

          if (updateError) {
            throw new Error(`Failed to update workspace: ${updateError.message}`)
          }
        }

        // Update the local state
        setAvatarPath(null)
        setAvatarUrl(null)
        setAvatarCleared(false)

        // Refresh the global workspace context so every surface that reads
        // `currentOrg.avatar_path` (sidebar, popovers, chat header, …)
        // re-renders without waiting for a page reload.
        await reloadWorkspaces()

        // Trigger avatar refresh callback
        onAvatarUpdated?.()
      }
      // Handle avatar upload if there's a cropped file
      else if (croppedFile) {
        // Use API route to upload avatar
        const formData = new FormData()
        formData.append('file', croppedFile)
        formData.append('action', 'upload')

        const uploadResponse = await fetch(`/api/workspaces/${currentOrg.id}/avatar`, {
          method: 'POST',
          body: formData
        })

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json()
          throw new Error(errorData.error || 'Failed to upload avatar')
        }

        const { avatarPath: newAvatarPath, avatarUrl: newAvatarUrl } = await uploadResponse.json()

        // Update workspace display name if it has changed
        if (displayName !== currentOrg.display_name) {
          const { error: updateError } = await supabase
            .from('workspaces')
            .update({ display_name: displayName })
            .eq('id', currentOrg.id)

          if (updateError) {
            throw new Error(`Failed to update workspace: ${updateError.message}`)
          }
        }

        // Update the local state
        setAvatarPath(newAvatarPath)
        setAvatarUrl(newAvatarUrl)
        setCroppedFile(null)
        setAvatarCleared(false)

        // Refresh the global workspace context so every surface that reads
        // `currentOrg.avatar_path` (sidebar, popovers, chat header, …)
        // re-renders without waiting for a page reload.
        await reloadWorkspaces()

        // Trigger avatar refresh callback
        onAvatarUpdated?.()
      } else {
        // Only update display name if it has changed
        if (displayName !== currentOrg.display_name) {
          const { error } = await supabase
            .from('workspaces')
            .update({ display_name: displayName })
            .eq('id', currentOrg.id);

          if (error) throw error;

          // Refresh the global workspace context so the new display name
          // propagates to every surface immediately.
          await reloadWorkspaces()
        }
      }

      toast.success('Workspace updated successfully');
    } catch (error) {
      console.error('Error updating workspace:', error);
      toast.error(`Failed to update workspace: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }



  // Team settings handlers
  const handleSendInvite = async () => {
    if (!currentOrg || !inviteEmail) return
    setInviteLoading(true)
    const target = inviteEmail.trim().toLowerCase()

    // Local duplicate guard (server enforces too, but this gives instant feedback).
    if (teamMembers.some(m => (m.public_profiles?.email || '').toLowerCase() === target)) {
      toast.error('That user is already a member — use the role dropdown next to their name to change their role.')
      setInviteLoading(false)
      return
    }
    if (teamInvitations.some(i => (i.email || '').toLowerCase() === target && i.status === 'pending')) {
      toast.error('That user already has a pending invitation — find it in the Pending Invitations list below.')
      setInviteLoading(false)
      return
    }

    try {
      const res = await fetch(`/api/invitations/create?workspaceId=${currentOrg.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: target,
          role: inviteRole,
        }),
      })
      const data = await res.json()
      if (res.ok) {
        toast.success(data.resent ? 'Invitation resent' : 'Invitation sent')
        setInviteEmail('')
        fetchTeam()
      } else {
        toast.error(data.error || 'Failed to send invite')
      }
    } catch {
      toast.error('Failed to send invite')
    }
    setInviteLoading(false)
  }

  const handleCancelInvite = async (inviteId: string) => {
    try {
      const res = await fetch(`/api/invitations/${inviteId}`, { method: 'DELETE' })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        toast.success('Invitation cancelled')
        fetchTeam()
      } else {
        toast.error(data?.error || 'Failed to cancel invitation')
      }
    } catch {
      toast.error('Failed to cancel invitation')
    } finally {
      setConfirmCancelInvite(null)
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    setPendingMemberAction(memberId)
    try {
      const res = await fetch(`/api/workspaces/members/${memberId}`, { method: 'DELETE' })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        toast.success(data.removed_self ? 'You left the workspace' : 'Member removed')
        if (data.removed_self) {
          // Caller no longer has access — bounce to dashboard.
          window.location.href = '/campaigns'
          return
        }
        fetchTeam()
      } else {
        toast.error(data?.error || 'Failed to remove member')
      }
    } catch {
      toast.error('Failed to remove member')
    } finally {
      setPendingMemberAction(null)
      setConfirmRemove(null)
    }
  }

  const handleChangeRole = async (memberId: string, newRole: Enums<'workspace_role'>) => {
    setPendingMemberAction(memberId)
    try {
      const res = await fetch(`/api/workspaces/members/${memberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        toast.success('Role updated')
        fetchTeam()
      } else {
        toast.error(data?.error || 'Failed to update role')
      }
    } catch {
      toast.error('Failed to update role')
    } finally {
      setPendingMemberAction(null)
    }
  }

  const canManageMembers = callerRole === 'owner' || callerRole === 'admin'
  const canInviteOwners = callerRole === 'owner'
  const normalizedInviteEmail = inviteEmail.trim().toLowerCase()
  const existingMemberMatch = useMemo(() => {
    if (!normalizedInviteEmail) return null
    return teamMembers.find(m => (m.public_profiles?.email || '').trim().toLowerCase() === normalizedInviteEmail) || null
  }, [normalizedInviteEmail, teamMembers])
  const pendingInvitationMatch = useMemo(() => {
    if (!normalizedInviteEmail || existingMemberMatch) return null
    return teamInvitations.find(i => (i.email || '').trim().toLowerCase() === normalizedInviteEmail && i.status === 'pending') || null
  }, [existingMemberMatch, normalizedInviteEmail, teamInvitations])
  const inviteBlockedByDuplicate = Boolean(existingMemberMatch || pendingInvitationMatch)

  // Billing settings handlers
  const handleOpenBillingPortal = async () => {
    if (!currentOrg) return
    setBillingLoading(true)
    
    try {
      const response = await fetch('/api/stripe/create-customer-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          returnUrl: window.location.href
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create billing portal session')
      }

      // Redirect to Stripe Customer Portal
      window.location.href = result.url
    } catch (error) {
      console.error('Error opening billing portal:', error)
      
      // Handle specific error cases
      if (error instanceof Error && error.message.includes('Customer Portal not configured')) {
        toast.error('Billing portal is not yet configured. Please contact support.')
      } else {
        toast.error('Failed to open billing portal')
      }
    } finally {
      setBillingLoading(false)
    }
  }

  if (!currentOrg) {
    return null
  }

  return (
    <div className="flex flex-col h-full bg-atrium-content">
      
        <Accordion type="single" value={activeSection} onValueChange={setActiveSection} collapsible>
          {/* General Settings */}
          <AccordionItem value="general" className="border-b border-atrium-border last:border-b-0">
            <AccordionTrigger className="px-4 py-3 text-atrium-black hover:text-accent-600">
              Workspace Information
            </AccordionTrigger>
            <AccordionContent className="p-4 pt-1">
              <div className="space-y-5 border border-atrium-border p-4 bg-atrium-inner rounded-lg">
                {/* Avatar Section */}
                <AvatarUploadTrigger
                  avatarFile={croppedFile}
                  avatarUrl={avatarUrl}
                  inputId="workspace-avatar-input"
                  label="Workspace Avatar"
                  disabled={loading}
                  submitting={loading}
                  onCropComplete={handleCropComplete}
                  onClear={() => {
                    setCroppedFile(null)
                    setAvatarUrl(null)
                    setAvatarPath(null)
                    setAvatarCleared(true)
                  }}
                />
                
                <div className="w-full">
                  <Label htmlFor="displayName" variant="atrium">Display Name</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="Enter workspace name"
                    variant="white"
                  />
                </div>

                {/* Workspace ID */}
                <div>
                  <Label variant="atrium">Workspace ID</Label>
                  <Input
                    value={currentOrg.id}
                    readOnly
                    disabled
                    variant="white"
                  />
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button 
                    variant="atrium" 
                    onClick={handleSave}
                    disabled={loading}
                    className="h-10"
                  >
                    {loading ? (
                      <>
                        <HugeiconsIcon icon={Loading03Icon} className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Team Settings */}
          <AccordionItem value="team" className="border-b border-atrium-border last:border-b-0">
            <AccordionTrigger className="px-4 py-3 text-atrium-black hover:text-accent-600">
              Team Management
            </AccordionTrigger>
            <AccordionContent className="p-4 pt-1">
              <div className="space-y-5 border border-atrium-border p-4 bg-atrium-inner rounded-lg">
                {canManageMembers ? (
                  <div className="space-y-3">
                    <p className="text-xs text-atrium-black/60 -mt-1 mb-2">
                      Inviting a new person? Enter their email below. To change an existing member&apos;s role, use the dropdown next to their name.
                    </p>
                    <div>
                      <Label htmlFor="inviteEmail" variant="atrium">Email address</Label>
                      <Input
                        id="inviteEmail"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        placeholder="name@company.com"
                        disabled={inviteLoading}
                        onKeyDown={e => { if (e.key === 'Enter') handleSendInvite() }}
                        variant="white"
                      />
                      {existingMemberMatch && (
                        <div className="flex gap-1.5 items-start mt-2 text-xs text-atrium-black/60">
                          <HugeiconsIcon icon={InformationCircleIcon} className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>
                            {existingMemberMatch.public_profiles?.display_name || existingMemberMatch.public_profiles?.email?.split('@')[0] || 'That user'} is already a <span className="capitalize">{existingMemberMatch.role}</span>. Use the role dropdown next to their name to change their role.
                          </span>
                        </div>
                      )}
                      {!existingMemberMatch && pendingInvitationMatch && (
                        <div className="flex gap-1.5 items-start mt-2 text-xs text-atrium-black/60">
                          <HugeiconsIcon icon={InformationCircleIcon} className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>That email already has a pending invitation. You can resend or cancel it below.</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label variant="atrium">Role</Label>
                      <Select value={inviteRole} onValueChange={v => setInviteRole(v as Enums<'workspace_role'>)}>
                        <SelectTrigger className="px-3 mt-2 h-10 text-atrium-black border bg-atrium-content border-atrium-border">
                          <SelectValue placeholder="Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          {canInviteOwners && <SelectItem value="owner">Owner</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="atrium"
                      onClick={handleSendInvite}
                      disabled={inviteLoading || !inviteEmail || inviteBlockedByDuplicate}
                      className="w-full h-10"
                    >
                      {inviteLoading ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-md border border-atrium-border bg-atrium-content px-3 py-2 text-sm text-atrium-black/60">
                    Only owners and admins can invite new members.
                  </div>
                )}

                {/* Members List */}
                <div className="space-y-3">
                  <h4 className="font-medium text-atrium-black">Members ({teamMembers.length})</h4>
                  {teamLoading ? (
                    <div className="text-sm text-atrium-black/60">Loading members...</div>
                  ) : teamMembers.length === 0 ? (
                    <div className="text-sm text-atrium-black/40">No members yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {teamMembers.map(member => {
                        const isSelf = Boolean(member.is_self)
                        const isOwner = member.role === 'owner'
                        const isOnlyOwner = isOwner && ownerCount <= 1
                        // Caller can edit role if: caller is owner OR (caller is admin AND target is not an owner).
                        const canEditRole = callerRole === 'owner' || (callerRole === 'admin' && !isOwner)
                        // Caller can remove if: self-remove (and not only owner), OR owner removing anyone else, OR admin removing a non-owner.
                        const canRemove = isOnlyOwner
                          ? false
                          : isSelf
                            ? true
                            : callerRole === 'owner' || (callerRole === 'admin' && !isOwner)
                        const displayName = member.public_profiles?.display_name || (member.public_profiles?.email?.split('@')[0]) || 'Unknown'
                        const busy = pendingMemberAction === member.id
                        return (
                          <div key={member.id} className="flex gap-3 items-center p-3 rounded-lg border bg-atrium-content border-atrium-border">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={getUserAvatarUrl(member.public_profiles?.avatar_path)} />
                              <AvatarFallback className="text-xs text-atrium-black bg-atrium-background">{displayName?.[0]?.toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-atrium-black truncate">
                                {displayName}
                                {isSelf && <span className="ml-1 text-xs font-normal text-atrium-black/40">(you)</span>}
                              </div>
                              <div className="text-xs truncate text-atrium-black/50">{member.public_profiles?.email || ''}</div>
                            </div>
                            {canEditRole ? (
                              <Select
                                value={member.role}
                                disabled={busy}
                                onValueChange={v => handleChangeRole(member.id, v as Enums<'workspace_role'>)}
                              >
                                <SelectTrigger className="h-8 w-[110px] text-xs capitalize bg-atrium-content border-atrium-border text-atrium-black">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="member">Member</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  {callerRole === 'owner' && <SelectItem value="owner">Owner</SelectItem>}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="px-2 py-1 text-xs capitalize rounded-full bg-atrium-background text-atrium-black/70">{member.role}</div>
                            )}
                            {canRemove && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busy}
                                onClick={() => setConfirmRemove({ id: member.id, name: displayName, self: isSelf })}
                                className="px-2 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              >
                                {isSelf ? 'Leave' : 'Remove'}
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Invitations List */}
                {teamInvitations.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-atrium-black">Pending Invitations ({teamInvitations.length})</h4>
                    <div className="space-y-2">
                      {teamInvitations.map(invite => {
                        const isRedundantInvite = teamMembers.some(member => (
                          (member.public_profiles?.email || '').trim().toLowerCase() === (invite.email || '').trim().toLowerCase()
                        ))

                        return (
                          <div key={invite.id} className="flex gap-3 items-center p-3 rounded-lg border bg-atrium-content border-atrium-border">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-atrium-black truncate">{invite.email}</div>
                              <div className="text-xs truncate text-atrium-black/50 capitalize">Role: {invite.role}</div>
                            </div>
                            <div className="px-2 py-1 text-xs capitalize rounded-full bg-amber-100 text-amber-700">{invite.status}</div>
                            {isRedundantInvite && (
                              <div
                                className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700"
                                title="This person is already a member of the workspace."
                              >
                                Redundant
                              </div>
                            )}
                            {canManageMembers && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setConfirmCancelInvite({ id: invite.id, email: invite.email })}
                                className="px-2 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Confirmation dialogs */}
                <AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {confirmRemove?.self ? 'Leave this workspace?' : `Remove ${confirmRemove?.name}?`}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {confirmRemove?.self
                          ? 'You\u2019ll lose access to this workspace immediately. You can re-join later if invited.'
                          : 'This person will lose access to the workspace immediately. You can re-invite them later.'}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => confirmRemove && handleRemoveMember(confirmRemove.id)}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                      >
                        {confirmRemove?.self ? 'Leave workspace' : 'Remove'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog open={!!confirmCancelInvite} onOpenChange={(open) => !open && setConfirmCancelInvite(null)}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel invitation?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The invitation for {confirmCancelInvite?.email} will be cancelled. They won\u2019t be able to use the existing link.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep invitation</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => confirmCancelInvite && handleCancelInvite(confirmCancelInvite.id)}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                      >
                        Cancel invitation
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Billing Settings */}
          <AccordionItem value="billing" className="border-b border-atrium-border last:border-b-0">
            <AccordionTrigger className="px-4 py-3 text-atrium-black hover:text-accent-600">
              Billing & Payments
            </AccordionTrigger>
            <AccordionContent className="p-4 pt-1">
              <div className="space-y-5 border border-atrium-border p-4 bg-atrium-inner rounded-lg">
                <div className="flex justify-center flex-col items-center p-4 rounded-lg border bg-atrium-content border-atrium-border space-y-4">
                  <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-accent-100 shrink-0">
                      <HugeiconsIcon icon={CreditCardIcon} size={16} className="text-accent-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-atrium-black">
                        {stripeCustomerId ? 'Billing Account Active' : 'No Billing Account'}
                      </p>
                      <p className="text-xs text-atrium-black/60">
                        {stripeCustomerId ? 'Manage your payment methods and billing' : 'Create account to manage billing'}
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    variant="atrium"
                    onClick={handleOpenBillingPortal}
                    disabled={billingLoading || !stripeCustomerId}
                    size="sm"
                    className="flex gap-2 items-center"
                  >
                    <HugeiconsIcon icon={CreditCardIcon} size={12} />
                    {billingLoading ? 'Opening...' : 'Manage Billing'}
                  </Button>
                </div>

                {!stripeCustomerId && (
                  <div className="flex gap-3 items-center p-4 rounded-lg border bg-yellow-500/10 border-yellow-500/20">
                    <HugeiconsIcon icon={Alert01Icon} className="w-4 h-4 text-yellow-400" />
                    <div>
                      <p className="text-sm font-medium text-atrium-black">No Billing Account</p>
                      <p className="text-xs text-atrium-black/60">
                        A billing account will be created automatically when you make your first purchase.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

        </Accordion>
    </div>
  )
}
